import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { X, PanelLeftClose, PanelLeftOpen } from '../../components/CustomIcons';

/* ==========================================================
   Toast + Modal — global, context-driven, single instance
   ========================================================== */

const UIContext = createContext(null);

export const UIProvider = ({ children }) => {
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);
  const triggerRef = useRef(null);

  const showToast = useCallback((message, tone = 'default') => {
    setToast({ message, tone, id: Date.now() });
  }, []);

  const showModal = useCallback((cfg) => {
    triggerRef.current = document.activeElement;
    setModal(cfg);
  }, []);
  const closeModal = useCallback(() => {
    setModal(null);
    setTimeout(() => { if (triggerRef.current && triggerRef.current.focus) triggerRef.current.focus(); }, 0);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  // ESC handler + scroll lock while modal open
  useEffect(() => {
    if (!modal) return;
    const onKey = (e) => { if (e.key === 'Escape') { if (modal.onCancel) modal.onCancel(); closeModal(); } };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [modal, closeModal]);

  const value = useMemo(() => ({ showToast, showModal, closeModal }), [showToast, showModal, closeModal]);

  return (
    <UIContext.Provider value={value}>
      {children}
      {toast && (
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-6 z-[200] rounded-[12px] px-4 py-3 shadow-lg max-w-[85%]"
          style={{
            background: '#0f2744', color: '#fafaf9',
            border: `1px solid ${toast.tone === 'success' ? '#15803d' : '#f97316'}`,
            animation: 'proto-toast-slide 220ms cubic-bezier(0.4, 1.4, 0.5, 1) both',
          }}
          role="status"
          aria-live="polite"
          data-testid="proto-toast"
        >
          <div
            key={toast.id}
            className="text-[14px] font-semibold leading-snug"
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              animation: `proto-toast-type ${Math.min(toast.message.length * 22, 900)}ms steps(${toast.message.length}) both`,
            }}
          >{toast.message}</div>
        </div>
      )}
      {modal && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center px-6" role="dialog" aria-modal="true" aria-labelledby="proto-modal-title">
          <div className="absolute inset-0 bg-black/55" onClick={() => { if (modal.onCancel) modal.onCancel(); closeModal(); }} />
          <div
            className="relative w-full max-w-sm rounded-[12px] p-5 z-[101]"
            style={{ background: '#fafaf9', maxHeight: '75%', overflowY: 'auto', animation: 'proto-fade-up 200ms ease-out both' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <h2 id="proto-modal-title" className="p-display text-[19px] leading-tight" style={{ color: '#0f2744', margin: 0 }}>{modal.title}</h2>
              <button
                onClick={() => { if (modal.onCancel) modal.onCancel(); closeModal(); }}
                data-testid="proto-modal-close"
                aria-label="Close dialog"
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#64748b] hover:bg-[#f1f5f9] shrink-0 -mt-1 -mr-1"
                autoFocus
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-2 text-[14px] text-[#1c1c1e] leading-relaxed">{modal.body}</div>
            {modal.input && (
              <textarea
                className="mt-3 w-full rounded-[8px] border border-[#cbd5e1] bg-white px-3 py-2 text-[14px] focus:outline-none focus:border-[#f97316]"
                rows={3}
                placeholder={modal.input.placeholder}
                value={modal.input.value}
                onChange={(e) => modal.input.onChange(e.target.value)}
              />
            )}
            <div className="mt-4 flex items-center justify-end gap-3">
              <button
                onClick={() => { if (modal.onCancel) modal.onCancel(); closeModal(); }}
                className="text-[14px] font-semibold text-[#64748b] px-3 py-2 hover:underline"
                data-testid="proto-modal-cancel"
              >
                {modal.cancelLabel || 'Cancel'}
              </button>
              <button
                onClick={() => { if (modal.onConfirm) modal.onConfirm(); closeModal(); }}
                className="min-h-[48px] rounded-[12px] font-semibold text-[15px] bg-[#f97316] text-[#0f2744] hover:brightness-105 px-6"
                data-testid="proto-modal-confirm"
              >
                {modal.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be inside UIProvider');
  return ctx;
};

/* ==========================================================
   StatCard — used across all role dashboards
   ========================================================== */

export const StatCard = ({ label, value, accent = false, unit, testId }) => (
  <div
    className="rounded-[12px] p-4"
    style={{ background: accent ? 'rgba(249,115,22,0.10)' : '#f1f5f9', border: `1px solid ${accent ? '#f97316' : 'transparent'}` }}
    data-testid={testId}
  >
    <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: accent ? '#c2410c' : '#64748b' }}>{label}</div>
    <div className="mt-1 flex items-baseline gap-1.5">
      <div className="p-mono text-[24px] leading-none" style={{ color: '#0f2744' }}>{value}</div>
      {unit && <div className="text-[13px] text-[#64748b] font-semibold">{unit}</div>}
    </div>
  </div>
);

/* ==========================================================
   SectionTitle
   ========================================================== */

export const SectionTitle = ({ children, right }) => (
  <div className="flex items-center justify-between mb-2">
    <h2 className="text-[12px] font-bold text-[#64748b] uppercase tracking-widest">{children}</h2>
    {right && <div className="text-[12px] font-semibold text-[#0f2744]">{right}</div>}
  </div>
);

/* ==========================================================
   BottomNav — persistent tabs (per-role) — legacy for learner
   ========================================================== */

export const BottomNav = ({ items, activeKey, onChange }) => (
  <nav
    className="grid border-t border-[#e2e8f0] bg-[#fafaf9]"
    style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))` }}
    aria-label="Role navigation"
  >
    {items.map((it) => {
      const active = it.key === activeKey;
      const Icon = it.icon;
      return (
        <button
          key={it.key}
          onClick={() => onChange(it.key)}
          data-testid={`proto-nav-${it.key}`}
          aria-current={active ? 'page' : undefined}
          className="min-h-[56px] flex flex-col items-center justify-center gap-0.5"
          style={{ color: active ? '#f97316' : '#64748b' }}
        >
          {Icon && <Icon className="w-4 h-4" aria-hidden="true" />}
          <span className="text-[11px] font-semibold" style={{ letterSpacing: '0.02em' }}>{it.label}</span>
        </button>
      );
    })}
  </nav>
);

/* ==========================================================
   Sidebar — persistent left rail for staff/admin roles.
   Widths driven by CSS on .proto-root[data-orientation]:
     - portrait  : 56px, icons only
     - landscape : 184px, icons + labels
     - landscape + collapsed : 56px, icons only (user-toggled)
   ========================================================== */

const SIDEBAR_COLLAPSE_KEY = 'assistivos_sidebar_collapsed';
const readSidebarCollapsed = () => {
  try { return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1'; } catch (_e) { return false; }
};

export const Sidebar = ({ items, activeKey, onChange, badge, footerUnread = 0, itemBadges = {} }) => {
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? '1' : '0'); } catch (_e) { /* ignore */ }
  }, [collapsed]);
  const toggle = () => setCollapsed((c) => !c);
  return (
    <aside
      className="proto-sidebar"
      aria-label="Role navigation"
      data-testid="proto-sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <div className="proto-sidebar-brand" aria-hidden="true">
        <div className="proto-sidebar-brand-mark">◆</div>
        <span className="proto-sidebar-brand-text">AssistivOS</span>
      </div>
      <button
        type="button"
        onClick={toggle}
        data-testid="proto-sidebar-collapse-toggle"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-pressed={collapsed}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="proto-sidebar-collapse"
      >
        {collapsed ? <PanelLeftOpen className="proto-sidebar-icon" aria-hidden="true" /> : <PanelLeftClose className="proto-sidebar-icon" aria-hidden="true" />}
        <span className="proto-sidebar-label">{collapsed ? 'Expand' : 'Collapse'}</span>
      </button>
      <nav className="proto-sidebar-nav" role="navigation">
        {items.map((it) => {
          const active = it.key === activeKey;
          const Icon = it.icon;
          const pipCount = itemBadges[it.key];
          return (
            <button
              key={it.key}
              onClick={() => onChange(it.key)}
              data-testid={`proto-nav-${it.key}`}
              aria-current={active ? 'page' : undefined}
              aria-label={pipCount ? `${it.label} — ${pipCount} unread` : it.label}
              className={`proto-sidebar-item${active ? ' is-active' : ''}`}
              title={it.label}
            >
              <span className="proto-sidebar-icon-wrap">
                {Icon && <Icon className="proto-sidebar-icon" aria-hidden="true" />}
                {pipCount > 0 && (
                  <span className="proto-sidebar-pip" data-testid={`proto-nav-pip-${it.key}`} aria-hidden="true">
                    {pipCount > 9 ? '9+' : pipCount}
                  </span>
                )}
              </span>
              <span className="proto-sidebar-label">{it.label}</span>
            </button>
          );
        })}
      </nav>
      {badge && (
        <div className="proto-sidebar-footer" data-testid="proto-sidebar-footer">
          <div className="proto-sidebar-brand-mark" style={{ background: '#f97316', color: '#0f2744', position: 'relative' }}>
            {badge.initial}
            {footerUnread > 0 && (
              <span className="proto-sidebar-footer-pip" data-testid="proto-sidebar-footer-pip" aria-label={`${footerUnread} unread notifications`}>
                {footerUnread > 9 ? '9+' : footerUnread}
              </span>
            )}
          </div>
          <div className="proto-sidebar-footer-text">
            <div className="proto-sidebar-footer-name">{badge.name}</div>
            <div className="proto-sidebar-footer-role">{badge.role}</div>
          </div>
        </div>
      )}
    </aside>
  );
};

/**
 * StaffShell — wraps a screen with the persistent Sidebar on the left
 * and the screen's own <ScreenHeader/> + <main/> to the right.
 * Callers pass their existing screen as `children`.
 * `itemBadges` and `footerUnread` are optional pip counts.
 */
export const StaffShell = ({ items, activeKey, onChange, badge, children, itemBadges, footerUnread }) => (
  <div className="proto-staff-shell">
    <Sidebar items={items} activeKey={activeKey} onChange={onChange} badge={badge} itemBadges={itemBadges} footerUnread={footerUnread} />
    <div className="proto-staff-content">
      {children}
    </div>
  </div>
);

/* ==========================================================
   Avatar — initials-based
   ========================================================== */

export const Avatar = ({ name, size = 36, tone = 'navy' }) => {
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('');
  const bg = tone === 'orange' ? '#f97316' : '#0f2744';
  const fg = tone === 'orange' ? '#0f2744' : '#fafaf9';
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold shrink-0"
      style={{ width: size, height: size, background: bg, color: fg, fontFamily: 'var(--p-font-mono)', fontSize: Math.round(size * 0.36) }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
};

/* ==========================================================
   Badge (status pill)
   ========================================================== */

export const Badge = ({ children, tone = 'neutral' }) => {
  const styles = {
    neutral: { background: '#e2e8f0', color: '#475569' },
    orange: { background: 'rgba(249,115,22,0.12)', color: '#c2410c' },
    success: { background: 'rgba(21,128,61,0.12)', color: '#15803d' },
    navy: { background: 'rgba(15,39,68,0.10)', color: '#0f2744' },
  };
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      style={styles[tone] || styles.neutral}
    >
      {children}
    </span>
  );
};
